import React, { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import MessageBubble from "./components/MessageBubble";
import ChatInput from "./components/ChatInput";
import TypingIndicator from "./components/TypingIndicator";
import logoBlue from "../../assets/landing-page/logo-blue.svg";

const STREAM_API_URL = "https://alyseiai.ibyteworkshop.com/chat/stream";
const SESSION_KEY = "alysei_chat_session_id";

const parseSseEvent = (rawEvent) => {
  const event = { type: "message", dataLines: [] };

  rawEvent.split(/\r?\n/).forEach((line) => {
    if (line.startsWith("event:")) {
      event.type = line.replace(/^event:\s*/, "").trim();
    }

    if (line.startsWith("data:")) {
      event.dataLines.push(line.replace(/^data:\s*/, ""));
    }
  });

  event.data = event.dataLines.join("\n");
  return event;
};

const parseSseData = (data) => {
  if (!data) return {};

  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

const waitForNextPaint = () => {
  if (typeof window === "undefined" || !window.requestAnimationFrame) {
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
};

const handleSseEvent = async (rawEvent, { onSession, onToken, onChips, onDone }) => {
  const sseEvent = parseSseEvent(rawEvent);
  const data = parseSseData(sseEvent.data);

  if (sseEvent.type === "session" && data.session_id) {
    onSession(data.session_id);
    return;
  }

  if (sseEvent.type === "token") {
    onToken(data.text || "");
    await waitForNextPaint();
    return;
  }

  if (sseEvent.type === "chips" || sseEvent.type === "meta") {
    onChips(data);
    return;
  }

  if (sseEvent.type === "done") {
    onDone(data);
  }
};

const streamChatResponse = async ({
  message,
  sessionId,
  signal,
  onSession,
  onToken,
  onChips,
  onDone,
}) => {
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let processedLength = 0;
    let buffer = "";
    let eventChain = Promise.resolve();

    const processText = (text) => {
      buffer += text;
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() || "";

      events.forEach((rawEvent) => {
        if (!rawEvent.trim()) return;

        eventChain = eventChain.then(() =>
          handleSseEvent(rawEvent, { onSession, onToken, onChips, onDone })
        );
      });
    };

    const abortRequest = () => {
      xhr.abort();
      reject(new DOMException("The operation was aborted.", "AbortError"));
    };

    xhr.open("POST", STREAM_API_URL, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader("Accept", "text/event-stream");

    xhr.onprogress = () => {
      const chunk = xhr.responseText.slice(processedLength);
      processedLength = xhr.responseText.length;
      processText(chunk);
    };

    xhr.onload = () => {
      eventChain
        .then(async () => {
          const chunk = xhr.responseText.slice(processedLength);
          processedLength = xhr.responseText.length;
          processText(chunk);
          await eventChain;

          if (buffer.trim()) {
            await handleSseEvent(buffer, { onSession, onToken, onChips, onDone });
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
            return;
          }

          reject(new Error(xhr.statusText || "Something went wrong. Please try again."));
        })
        .catch(reject);
    };

    xhr.onerror = () => {
      reject(new Error("Something went wrong. Please try again."));
    };

    xhr.onabort = () => {
      reject(new DOMException("The operation was aborted.", "AbortError"));
    };

    signal?.addEventListener("abort", abortRequest, { once: true });

    xhr.send(JSON.stringify({
      session_id: sessionId || "",
      message,
    }));
  });
};

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-full text-center px-6 py-16">
    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5" style={{ backgroundColor: "#f0fdfa" }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    </div>
    <h2 className="text-lg font-bold text-gray-800 mb-2">How can I help you?</h2>
    <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
      Describe your sourcing needs and I'll match you with relevant Italian food & beverage producers.
    </p>
  </div>
);

const ErrorBanner = ({ error, onDismiss }) => {
  if (!error) return null;
  return (
    <div className="mx-4 mb-3 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <span className="flex-1">{error}</span>
      <button onClick={onDismiss} className="ml-auto text-red-400 hover:text-red-600 font-bold text-base leading-none">×</button>
    </div>
  );
};

const ChatPage = () => {
  const history = useHistory();
  const location = useLocation();

  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [waitingForFirstToken, setWaitingForFirstToken] = useState(false);
  const [error, setError] = useState(null);

  const messagesEndRef = useRef(null);
  const firstMessageSent = useRef(false);
  const streamControllerRef = useRef(null);
  const introSent = useRef(false);

  // Scroll to bottom on new messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  // Send a message to the API
  const sendMessage = useCallback(async (text, currentSessionId, isEdit = false, editIndex = null, isIntro = false) => {
    const trimmedText = text.trim();
    const createdAt = Date.now();
    const assistantMsgId = createdAt + 1;
    const userMsg = {
      id: createdAt,
      role: "user",
      text: trimmedText,
      timestamp: createdAt,
    };
    const assistantMsg = {
      id: assistantMsgId,
      role: "assistant",
      text: "",
      timestamp: createdAt,
      quickReplies: null,
      categoriesGrouped: null,
    };

    if (!isIntro) {
      setMessages((prev) => {
        if (isEdit && editIndex !== null) {
          return [...prev.slice(0, editIndex), userMsg];
        }
        return [...prev, userMsg];
      });
    }

    setLoading(true);
    setWaitingForFirstToken(true);
    setError(null);

    try {
      const controller = new AbortController();
      streamControllerRef.current = controller;
      let hasReplyText = false;
      let collectedChips = null;

      setMessages((prev) => [...prev, assistantMsg]);

      await streamChatResponse({
        message: isIntro ? "" : trimmedText,
        sessionId: currentSessionId,
        signal: controller.signal,
        onSession: (newSessionId) => {
          setSessionId(newSessionId);
          sessionStorage.setItem(SESSION_KEY, newSessionId);
        },
        onToken: (token) => {
          if (!token) return;

          setWaitingForFirstToken(false);
          hasReplyText = true;
          flushSync(() => {
            setMessages((prev) =>
              prev.map((messageItem) =>
                messageItem.id === assistantMsgId
                  ? { ...messageItem, text: `${messageItem.text}${token}` }
                  : messageItem
              )
            );
          });
        },
        onChips: (chipsData) => {
          // New API format: { display_mode: "chips", options: [...strings] }
          if (chipsData?.display_mode === "chips" && chipsData?.options?.length) {
            collectedChips = {
              quick_replies: chipsData.options.map((opt) => ({ label: opt, value: opt })),
            };
          } else if (chipsData?.quick_replies?.length) {
            collectedChips = chipsData;
          }
        },
        onDone: () => {},
      });

      // Apply collected chips and/or fallback text after stream completes
      setMessages((prev) =>
        prev.map((messageItem) => {
          if (messageItem.id !== assistantMsgId) return messageItem;
          const update = {};
          if (!hasReplyText) update.text = "I'm sorry, I couldn't process your request.";
          if (collectedChips) {
            update.quickReplies = collectedChips.quick_replies;
            update.categoriesGrouped = collectedChips.categories_grouped || null;
          }
          return { ...messageItem, ...update };
        })
      );
    } catch (err) {
      if (err?.name === "AbortError") return;

      const msg =
        err?.message ||
        "Something went wrong. Please try again.";
      setError(msg);
      setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMsgId));
    } finally {
      streamControllerRef.current = null;
      setLoading(false);
      setWaitingForFirstToken(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      streamControllerRef.current?.abort();
    };
  }, []);

  // Handle initial message passed from homepage, or send blank intro to load categories
  useEffect(() => {
    if (firstMessageSent.current) return;
    firstMessageSent.current = true;
    sessionStorage.removeItem(SESSION_KEY);

    const initialMessage = location?.state?.initialMessage;
    if (initialMessage && initialMessage.trim()) {
      history.replace({ ...location, state: null });
      sendMessage(initialMessage, "");
    } else {
      sendMessage("", "", false, null, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = () => {
    if (!inputValue.trim() || loading) return;
    sendMessage(inputValue, sessionId);
    setInputValue("");
  };

  const handleQuickReply = (value) => {
    if (loading) return;
    sendMessage(value, sessionId);
  };

  const handleBack = () => {
    history.push("/");
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ backgroundColor: "#f7f8fa", fontFamily: "SF Pro Display, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ── Header: Logo LEFT · Badge CENTER · Back RIGHT ── */}
      <header className="flex-shrink-0 sticky top-0 z-20 bg-white border-b border-gray-100" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div className="relative flex items-center h-14 md:h-16 px-4 md:px-8">

          {/* LEFT — Logo */}
         

          {/* CENTER — absolutely positioned so it's always truly centred */}
          {/* <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full select-none" style={{ color: "#0f766e", backgroundColor: "#f0fdfa", border: "1px solid #99f6e4" }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#14b8a6" }} />
              AI Online
            </div>
          </div> */}

          {/* RIGHT — Back to Home */}
          <button
            onClick={handleBack}
            className="z-10 flex items-center gap-1 rounded-md border-0 md:border border-gray-200 bg-white px-5 py-1 text-sm font-semibold text-gray-700 shadow-0 md:shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 whitespace-nowrap"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            <span className="hidden md:block">Back</span>
          </button>
           <a href="/" className="absolute left-1/2 top-1/2 flex items-center chat--logo">
            <img src={logoBlue} alt="Alysei" className="h-6 md:h-7" />
          </a>
        </div>
      </header>

      {/* ── Subtitle ── */}
      <div className="flex-shrink-0 text-center py-2 border-b border-gray-100 bg-white/60">
        <span className="text-gray-500 tracking-wide select-none" style={{ fontSize: "14px" }}>
          Alysei AI · Italian Food &amp; Beverage Sourcing Assistant
        </span>
      </div>

      {/* ── Messages area ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto w-full px-4 md:px-6 py-4 flex flex-col gap-5 min-h-full">
          {messages.map((msg) => {
            if (msg.role === "assistant" && !msg.text && !msg.quickReplies) return null;
            return (
              <MessageBubble
                key={msg.id}
                message={msg}
                onQuickReply={handleQuickReply}
                isLoading={loading}
              />
            );
          })}

          {waitingForFirstToken && <TypingIndicator />}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* ── Error banner ── */}
      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {/* ── Input area ── */}
      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSend}
        loading={loading}
      />

      {/* Keyframe animations */}
      <style>{`
        @keyframes typing-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
        @keyframes alysei-fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: alysei-fadeIn 0.28s ease-out both;
        }
      `}</style>
    </div>
  );
};

export default ChatPage;
